/* eslint-disable react/jsx-sort-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  ChartEmpty,
  CHART_HEIGHT_RANKING,
  buildCargaStackedSeries,
  getCargaChartLayout,
  mapCargaToDataset,
  PRODUTIVIDADE_INLINE_MAX_HEIGHT,
  PRODUTIVIDADE_ROW_HEIGHT,
} from './chartShared';

const chartWrapperSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  '& .MuiChartsAxis-root': { '& text': { textAnchor: 'start' } },
};

const formatCargaTooltip = (row, dataKey, value) => {
  if (!row) return String(value);
  if (dataKey === 'servicosDigitados') {
    return `${value} digitados`;
  }
  if (dataKey === 'servicosPendentes') {
    return `${value} pendentes`;
  }
  return String(value);
};

/** Barras horizontais empilhadas: serviços digitados vs pendentes por digitador. */
const DigitadorCargaChart = ({ items, loading, height }) => {
  const theme = useTheme();
  const isExpanded = height != null;

  const dataset = useMemo(() => mapCargaToDataset(items), [items]);
  const chartLayout = useMemo(() => getCargaChartLayout(dataset), [dataset]);
  const series = useMemo(() => buildCargaStackedSeries(theme), [theme]);
  const scrollableHeight = Math.max(
    CHART_HEIGHT_RANKING,
    dataset.length * PRODUTIVIDADE_ROW_HEIGHT + 56,
  );
  const chartHeight = isExpanded ? (height || scrollableHeight) : scrollableHeight;
  const hasData = dataset.length > 0;

  const seriesWithFormatters = useMemo(
    () =>
      series.map((s) => ({
        ...s,
        valueFormatter: (value, context) => {
          const row = dataset[context?.dataIndex];
          return formatCargaTooltip(row, s.dataKey, value);
        },
      })),
    [series, dataset],
  );

  const chart = (
    <BarChart
      layout="horizontal"
      dataset={dataset}
      series={seriesWithFormatters}
      yAxis={[
        {
          scaleType: 'band',
          dataKey: 'label',
          width: chartLayout.yAxisWidth,
          tickLabelStyle: {
            fontSize: 11,
            textAnchor: 'start',
          },
        },
      ]}
      xAxis={[{ min: 0 }]}
      height={chartHeight}
      margin={chartLayout.margin}
      slotProps={{
        legend: {
          direction: 'row',
          position: { vertical: 'bottom', horizontal: 'middle' },
        },
      }}
      sx={{ width: '100%', maxWidth: '100%', ml: 0 }}
    />
  );

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={isExpanded ? chartHeight : PRODUTIVIDADE_INLINE_MAX_HEIGHT}
      />
    );
  }

  if (!hasData) {
    return <ChartEmpty message="Sem dados no período selecionado" />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {isExpanded ? (
        <Box sx={chartWrapperSx}>{chart}</Box>
      ) : (
        <Box
          sx={{
            ...chartWrapperSx,
            maxHeight: PRODUTIVIDADE_INLINE_MAX_HEIGHT,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {chart}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Ordenado por serviços pendentes. Tooltip: total de NSIRs e serviços por digitador.
      </Typography>
    </Box>
  );
};

DigitadorCargaChart.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  height: PropTypes.number,
};

DigitadorCargaChart.defaultProps = {
  items: [],
  loading: false,
  height: null,
};

export default DigitadorCargaChart;
